import React, { useState, useEffect } from "react";
import { FaSearch, FaEdit, FaTrash, FaPlus, FaArrowLeft, FaMoneyCheckAlt } from "react-icons/fa";
import Navbar from "./Navbar";
import { useNavigate } from "react-router-dom";
import axiosInstance from "../config/axios";
import { toast } from "react-toastify";

const OutsideBrokersDB = () => {
  const [brokers, setBrokers] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState("");
  const [editingBroker, setEditingBroker] = useState(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [brokerToDelete, setBrokerToDelete] = useState(null);
  const navigate = useNavigate();

  // Agents list for linking to broker (edit modal)
  const [agents, setAgents] = useState([]);
  // EFT modal state
  const [showEftModal, setShowEftModal] = useState(false);
  const [eftBroker, setEftBroker] = useState(null);
  const [trades, setTrades] = useState([]);
  const [eftTradeId, setEftTradeId] = useState("");
  const [eftAmount, setEftAmount] = useState("");
  const [eftChequeDate, setEftChequeDate] = useState(
    new Date().toISOString().split("T")[0]
  );
  const [isEftSubmitting, setIsEftSubmitting] = useState(false);

  // Form state for add/edit
  const [formData, setFormData] = useState({
    type: "Listing Broker",
    firstName: "",
    lastName: "",
    company: "",
    email: "",
    payBroker: "No",
    end: "Listing End",
    primaryPhone: "",
    chargedHST: "Yes",
    address: "", // Keep for backward compatibility
    streetNumber: "",
    streetName: "",
    unit: "",
    city: "",
    province: "",
    postalCode: "",
    agents: [], // [{ agentId, agentName }]
  });

  // Filter brokers based on search query
  const filteredBrokers = brokers.filter((broker) =>
    `${broker.firstName} ${broker.lastName} ${broker.company} ${broker.email}`
      .toLowerCase()
      .includes(searchQuery.toLowerCase())
  );

  useEffect(() => {
    fetchBrokers();
  }, []);

  // Fetch agents when add/edit modal is open (for linking agents to broker)
  useEffect(() => {
    if (showAddModal) {
      const fetchAgents = async () => {
        try {
          const res = await axiosInstance.get("/agents");
          setAgents(res.data || []);
        } catch (err) {
          console.error("Error fetching agents:", err);
          toast.error("Failed to load agents");
        }
      };
      fetchAgents();
    }
  }, [showAddModal]);

  const fetchBrokers = async () => {
    setIsLoading(true);
    try {
      const response = await axiosInstance.get("/outside-brokers");
      setBrokers(response.data);
    } catch (error) {
      console.error("Error fetching brokers:", error);
      toast.error("Failed to fetch brokers");
    } finally {
      setIsLoading(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      end:
        name === "type"
          ? value === "Listing Broker"
            ? "Listing End"
            : "Selling End"
          : prev.end,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = {
      ...formData,
      agents: Array.isArray(formData.agents) ? formData.agents : [],
    };
    try {
      if (editingBroker) {
        await axiosInstance.put(
          `/outside-brokers/${editingBroker._id}`,
          payload
        );
        toast.success("Broker updated successfully");
      } else {
        await axiosInstance.post("/outside-brokers", payload);
        toast.success("Broker added successfully");
      }
      setShowAddModal(false);
      setEditingBroker(null);
      resetForm();
      fetchBrokers();
    } catch (error) {
      console.error("Error saving broker:", error);
      toast.error("Failed to save broker");
    }
  };

  const handleEdit = (broker) => {
    setEditingBroker(broker);
    setFormData({
      type: broker.type,
      firstName: broker.firstName,
      lastName: broker.lastName,
      company: broker.company || "",
      email: broker.email || "",
      payBroker: broker.payBroker,
      end: broker.end,
      primaryPhone: broker.primaryPhone || "",
      chargedHST: broker.chargedHST,
      address: broker.address || "",
      streetNumber: broker.streetNumber || "",
      streetName: broker.streetName || "",
      unit: broker.unit || broker.unitNumber || "",
      city: broker.city || "",
      province: broker.province || "",
      postalCode: broker.postalCode || "",
      agents: Array.isArray(broker.agents) ? [...broker.agents] : [],
    });
    setShowAddModal(true);
  };

  const handleDelete = (broker) => {
    setBrokerToDelete(broker);
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    try {
      await axiosInstance.delete(`/outside-brokers/${brokerToDelete._id}`);
      toast.success("Broker deleted successfully");
      fetchBrokers();
    } catch (error) {
      console.error("Error deleting broker:", error);
      toast.error("Failed to delete broker");
    } finally {
      setShowDeleteConfirm(false);
      setBrokerToDelete(null);
    }
  };

  const resetForm = () => {
    setFormData({
      type: "Listing Broker",
      firstName: "",
      lastName: "",
      company: "",
      email: "",
      payBroker: "No",
      end: "Listing End",
      primaryPhone: "",
      chargedHST: "Yes",
      address: "",
      streetNumber: "",
      streetName: "",
      unit: "",
      city: "",
      province: "",
      postalCode: "",
      agents: [],
    });
  };

  const addAgentToBroker = (agent) => {
    if (!agent || !agent._id) return;
    const alreadyAdded = (formData.agents || []).some((a) => a.agentId === String(agent._id));
    if (alreadyAdded) return;
    const agentName = [agent.firstName, agent.lastName].filter(Boolean).join(" ") || "Agent";
    setFormData((prev) => ({
      ...prev,
      agents: [...(prev.agents || []), { agentId: String(agent._id), agentName }],
    }));
  };

  const removeAgentFromBroker = (agentId) => {
    setFormData((prev) => ({
      ...prev,
      agents: (prev.agents || []).filter((a) => a.agentId !== String(agentId)),
    }));
  };

  const openEftModal = async (broker) => {
    setEftBroker(broker);
    setEftTradeId("");
    setEftAmount("");
    setEftChequeDate(new Date().toISOString().split("T")[0]);
    setShowEftModal(true);
    try {
      const res = await axiosInstance.get("/trades");
      setTrades(res.data || []);
    } catch (err) {
      console.error("Error fetching trades:", err);
      toast.error("Failed to load trades");
    }
  };

  const submitEft = async (e) => {
    e.preventDefault();
    if (!eftBroker || !eftTradeId || !eftAmount || parseFloat(eftAmount) <= 0) {
      toast.error("Please select a trade and enter a valid amount.");
      return;
    }
    setIsEftSubmitting(true);
    try {
      const recipient = eftBroker.company
        ? `${eftBroker.firstName} ${eftBroker.lastName} - ${eftBroker.company}`
        : `${eftBroker.firstName} ${eftBroker.lastName}`;
      const rawDate = (eftChequeDate || "").split("T")[0];
      const [y, m, d] = rawDate.split("-");
      const formattedChequeDate = y && m && d ? `${m}/${d}/${y}` : null;
      await axiosInstance.post("/commission-trust-eft/outside-broker", {
        tradeId: eftTradeId,
        amount: parseFloat(eftAmount),
        recipient,
        description: "Transfer commission to outside broker",
        chequeDate: formattedChequeDate || undefined,
      });
      toast.success("EFT issued successfully");
      setShowEftModal(false);
      setEftBroker(null);
    } catch (err) {
      console.error("Error issuing EFT:", err);
      toast.error(err.response?.data?.message || "Failed to issue EFT");
    } finally {
      setIsEftSubmitting(false);
    }
  };

  const openAddModal = () => {
    setEditingBroker(null);
    resetForm();
    setShowAddModal(true);
  };

  const closeModal = () => {
    setShowAddModal(false);
    setEditingBroker(null);
    resetForm();
  };

  if (isLoading) {
    return (
      <div className="bg-gray-100 min-h-screen">
        <Navbar />
        <div className="p-8">
          <div className="flex justify-center items-center h-64">
            <div className="text-lg">Loading brokers...</div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-100 min-h-screen">
      <Navbar />
      <div className="p-8">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-gray-800">
            Outside Brokers Database
          </h1>
          <div className="flex gap-3">
            <button
              onClick={() => navigate("/database")}
              className="bg-gray-600 text-white px-4 py-2 rounded-lg hover:bg-gray-700 flex items-center gap-2"
            >
              <FaArrowLeft /> Back to Database
            </button>
            <button
              onClick={openAddModal}
              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 flex items-center gap-2"
            >
              <FaPlus /> Add New Broker
            </button>
          </div>
        </div>

        {/* Search Bar */}
        <div className="mb-6">
          <div className="relative">
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search brokers..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
        </div>

        {/* Brokers Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Name
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Company
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Email
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Pay Broker
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBrokers.map((broker) => (
                  <tr key={broker._id} className="hover:bg-gray-50">
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {broker.type}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {broker.firstName} {broker.lastName}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {broker.company || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {broker.email || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {broker.primaryPhone || "-"}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {broker.payBroker}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                      <div className="flex flex-wrap items-center gap-2">
                        <button
                          type="button"
                          onClick={() => openEftModal(broker)}
                          className="text-green-600 hover:text-green-800 inline-flex items-center gap-1"
                          title="Issue EFT"
                        >
                          <FaMoneyCheckAlt /> EFT
                        </button>
                        <button
                          type="button"
                          onClick={() => handleEdit(broker)}
                          className="text-blue-600 hover:text-blue-900"
                          title="Edit"
                        >
                          <FaEdit />
                        </button>
                        <button
                          type="button"
                          onClick={() => handleDelete(broker)}
                          className="text-red-600 hover:text-red-900"
                          title="Delete"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {filteredBrokers.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            {searchQuery
              ? "No brokers found matching your search."
              : "No brokers found."}
          </div>
        )}
      </div>

      {/* Add/Edit Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <h2 className="text-2xl font-bold mb-4">
              {editingBroker ? "Edit Broker" : "Add New Broker"}
            </h2>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Type *
                  </label>
                  <select
                    name="type"
                    value={formData.type}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Listing Broker">Listing Broker</option>
                    <option value="Cooperating Broker">
                      Cooperating Broker
                    </option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Pay Broker
                  </label>
                  <select
                    name="payBroker"
                    value={formData.payBroker}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleInputChange}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Company
                  </label>
                  <input
                    type="text"
                    name="company"
                    value={formData.company}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Primary Phone
                  </label>
                  <input
                    type="tel"
                    name="primaryPhone"
                    value={formData.primaryPhone}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Charged HST
                  </label>
                  <select
                    name="chargedHST"
                    value={formData.chargedHST}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="Yes">Yes</option>
                    <option value="No">No</option>
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Linked Agents
                  </label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {(formData.agents || []).map((a) => (
                      <span
                        key={a.agentId}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm"
                      >
                        {a.agentName}
                        <button
                          type="button"
                          onClick={() => removeAgentFromBroker(a.agentId)}
                          className="text-blue-600 hover:text-red-600 font-bold"
                          aria-label="Remove agent"
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                  <select
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    value=""
                    onChange={(e) => {
                      const id = e.target.value;
                      if (!id) return;
                      const agent = agents.find((a) => String(a._id) === id);
                      addAgentToBroker(agent);
                      e.target.value = "";
                    }}
                  >
                    <option value="">Add an agent...</option>
                    {agents
                      .filter(
                        (a) =>
                          !(formData.agents || []).some(
                            (x) => x.agentId === String(a._id)
                          )
                      )
                      .map((agent) => (
                        <option key={agent._id} value={agent._id}>
                          {agent.firstName} {agent.lastName}
                          {agent.employeeNo ? ` (${agent.employeeNo})` : ""}
                        </option>
                      ))}
                  </select>
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Number
                  </label>
                  <input
                    type="text"
                    name="streetNumber"
                    value={formData.streetNumber}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div className="md:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Street Name
                  </label>
                  <input
                    type="text"
                    name="streetName"
                    value={formData.streetName}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Unit
                  </label>
                  <input
                    type="text"
                    name="unit"
                    value={formData.unit}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    City
                  </label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Province
                  </label>
                  <input
                    type="text"
                    name="province"
                    value={formData.province}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Postal Code
                  </label>
                  <input
                    type="text"
                    name="postalCode"
                    value={formData.postalCode}
                    onChange={handleInputChange}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  {editingBroker ? "Update" : "Add"} Broker
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-4">
              Confirm Delete
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Are you sure you want to delete {brokerToDelete?.firstName}{" "}
              {brokerToDelete?.lastName}? This action cannot be undone.
            </p>
            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Issue EFT Modal */}
      {showEftModal && eftBroker && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-md">
            <h3 className="text-lg font-medium text-gray-900 mb-2">
              Issue EFT for Outside Broker
            </h3>
            <p className="text-sm text-gray-500 mb-4">
              {eftBroker.firstName} {eftBroker.lastName}
              {eftBroker.company ? ` - ${eftBroker.company}` : ""}
            </p>
            <form onSubmit={submitEft} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Trade *
                </label>
                <select
                  value={eftTradeId}
                  onChange={(e) => setEftTradeId(e.target.value)}
                  required
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                >
                  <option value="">Select a trade</option>
                  {trades.map((t) => (
                    <option key={t._id} value={t._id}>
                      #{t.tradeNumber || t._id} {t.keyInfo?.streetNumber} {t.keyInfo?.streetName}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Amount *
                </label>
                <input
                  type="number"
                  step="0.01"
                  min="0"
                  value={eftAmount}
                  onChange={(e) => setEftAmount(e.target.value)}
                  required
                  placeholder="0.00"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Cheque Date
                </label>
                <input
                  type="date"
                  value={eftChequeDate}
                  onChange={(e) => setEftChequeDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="flex justify-end space-x-3 pt-2">
                <button
                  type="button"
                  onClick={() => {
                    setShowEftModal(false);
                    setEftBroker(null);
                  }}
                  className="px-4 py-2 text-gray-700 bg-gray-200 rounded-md hover:bg-gray-300"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isEftSubmitting}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {isEftSubmitting ? "Issuing..." : "Issue EFT"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default OutsideBrokersDB;
